import {
  type ChangeEvent,
  type FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { isNil, isString } from 'lodash-es';

import {
  DATE_WITH_TIMEZONE_FORMAT,
  DEFAULT_DATE_FORMAT,
} from '@iconik-io/core/src/date/date_formats';
import type { AriaProps } from '@iconik-io/shared-lib/src/react/a11y/aria';
import { useLocale } from '@iconik-io/uad';

import DatePicker, { type NonStrictDateRange } from '../calendar/date_picker';
import {
  type CalendarDropdownOption,
  calendarOptionsDropdown,
  calendarOptionsPresets,
  type DateRange,
  type FormatSelectValue,
  type MatchSelectValue,
  matchSelectValue,
  type relativeToDate as relativeToDateFn,
  type StrictDateRange,
} from '../calendar/default_options';
import type { FormFieldEvent } from '../forms/types';
import IconButton from '../icon_button/icon_button';
import PopoverTrigger, {
  type PopoverTriggerHandle,
} from '../popover_trigger/popover_trigger';
import CalendarTrigger from './calendar_trigger';
import {
  formatCalendarDate,
  formatDate,
  formatRelativeDate,
  getOutputValue,
  getTimeZoneName,
  parseInput,
} from './utils';

import styles from './input_date.module.css';

const parseDate = (val: string): boolean => {
  const date = Date.parse(val);
  return !Number.isNaN(date);
};

const isDate = (
  val: string | NonStrictDateRange | undefined,
): val is string | StrictDateRange => {
  if (!val) {
    return false;
  }
  if (isString(val)) {
    return parseDate(val);
  }
  if (val.min && val.max) {
    return parseDate(val.min) && parseDate(val.max);
  }
  return false;
};

export type ValueType = NonStrictDateRange | string;

export type InputDateProps = {
  name?: string;
  placeholder?: string;
  value?: ValueType;
  disabled?: boolean;
  readOnly?: boolean;
  canClear?: boolean;
  dateFormat?: string;
  optionsDropdown?: CalendarDropdownOption[];
  optionsPresets?: CalendarDropdownOption[];
  matchSelectValue?: MatchSelectValue;
  formatSelectValue?: FormatSelectValue;
  showCustomOptions?: boolean;
  isRangeMode?: boolean;
  error?: boolean;
  outputFormat?: string;
  validations?: {
    maxDate?: Date | string;
    minDate?: Date | string;
  };
  initialMonth?: Date;
  onChange?: (value: ValueType, event?: FormFieldEvent) => void;
  onValueChange?: (value: ValueType, event: FormFieldEvent) => void;
  relativeToDate?: typeof relativeToDateFn;
  isDynamicWidth?: boolean;
  withTimePicker?: boolean;
  showTimeZone?: boolean;
  isPickerOnly?: boolean;
  placeholderValue?: Date;
  aria?: AriaProps;
};

const InputDate: FC<InputDateProps> = (props) => {
  const {
    value,
    canClear = true,
    error,
    isRangeMode = true,
    dateFormat = DEFAULT_DATE_FORMAT,
    outputFormat = DATE_WITH_TIMEZONE_FORMAT,
    showCustomOptions = true,
    optionsDropdown = calendarOptionsDropdown,
    optionsPresets = calendarOptionsPresets,
    validations,
    initialMonth,
    relativeToDate,
    onChange,
    onValueChange,
    disabled,
    isDynamicWidth = false,
    withTimePicker = false,
    showTimeZone = false,
    isPickerOnly = false,
  } = props;

  const { locale } = useLocale();
  const timeZoneName =
    showTimeZone && isString(value) && isDate(value)
      ? getTimeZoneName(new Date(value), locale)
      : undefined;

  const popoverTriggerHandle = useRef<PopoverTriggerHandle>(null);
  const [displayValue, setDisplayValue] = useState<string | undefined>();
  const [calendarValue, setCalendarValue] = useState<
    string | Date | DateRange | undefined
  >();

  const setValue = useCallback(
    (val: string | NonStrictDateRange | undefined): void => {
      if (isNil(val)) {
        return;
      }
      if (isDate(val)) {
        setDisplayValue(formatDate(val, dateFormat));
        setCalendarValue(formatCalendarDate(val));
      } else {
        setDisplayValue(
          formatRelativeDate(
            val,
            optionsPresets,
            optionsDropdown,
            matchSelectValue,
          ),
        );
        setCalendarValue(formatCalendarDate(val));
      }
    },
    [dateFormat, optionsDropdown, optionsPresets],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: setValue in deps requires proper memoization in all usages
  useEffect(() => {
    setValue(value);
  }, [value]);

  const updateValue = (
    date: NonStrictDateRange | string,
    event: FormFieldEvent,
  ): void => {
    const output = getOutputValue(date, outputFormat) ?? '';
    onValueChange?.(output, event);
    onChange?.(output, event);
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDisplayValue(event.currentTarget.value);
    if (!event.currentTarget.value) {
      updateValue(event.currentTarget.value, event);
    } else {
      const date = parseInput(
        event.currentTarget.value,
        outputFormat,
        dateFormat,
        isRangeMode,
        optionsPresets,
        optionsDropdown,
      );
      if (date) {
        updateValue(date, event);
      }
    }
  };

  const onChangeCalendar = (
    date: NonStrictDateRange | string,
    event: FormFieldEvent,
  ): void => {
    updateValue(date, event);
    popoverTriggerHandle.current?.close();
  };

  const onClose = (): void => {
    popoverTriggerHandle.current?.close();
  };

  const onClear = (event: FormFieldEvent): void => {
    if (event) {
      event.stopPropagation();
    }
    setValue('');
    onValueChange?.('', event);
    onChange?.('', event);
    popoverTriggerHandle.current?.close();
  };

  let minDate;
  let maxDate;
  if (validations) {
    minDate = validations.minDate;
    maxDate = validations.maxDate;
  }
  const readOnly = props.readOnly || disabled;
  return (
    <PopoverTrigger
      ref={popoverTriggerHandle}
      triggerElement={
        <CalendarTrigger
          canClear={Boolean(value && canClear && !readOnly)}
          readOnly={readOnly}
          onClear={onClear}
          inputProps={props}
          value={displayValue}
          onInputChange={onInputChange}
          error={error === true}
          isDynamicWidth={isDynamicWidth}
          withTimePicker={withTimePicker}
          showTimeZone={showTimeZone}
          timeZoneName={timeZoneName}
          isPickerOnly={isPickerOnly}
        />
      }
    >
      {!readOnly && (
        <div className={styles.calendar}>
          <IconButton
            className={styles.close}
            iconName="close"
            onClick={onClose}
          />
          <DatePicker
            onChange={onChangeCalendar}
            optionsDropdown={optionsDropdown}
            optionsPresets={optionsPresets}
            isRangeMode={isRangeMode}
            value={calendarValue || {}}
            showCustomOptions={showCustomOptions}
            dateFormat={outputFormat}
            minDate={minDate}
            maxDate={maxDate}
            initialMonth={initialMonth}
            relativeToDate={relativeToDate}
            withTimePicker={props.withTimePicker}
            placeholderValue={props.placeholderValue}
          />
        </div>
      )}
    </PopoverTrigger>
  );
};

export default InputDate;
